import React, { useEffect, useState } from "react";

const loginInitial = { username: "", password: "" };
const workDoneInitial = {
  work_order: "",
  container_work: "",
  work_date: new Date().toISOString().slice(0, 10),
  hours_worked: "0",
  containers_completed: "0",
  pallets_built: "0",
  pieces_handled: "0",
  notes: "",
};

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : "";
}

export default function EmployeePortal() {
  const [loginForm, setLoginForm] = useState(loginInitial);
  const [employee, setEmployee] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [resources, setResources] = useState({ notices: [], infoLinks: [], payStubs: [] });
  const [workDone, setWorkDone] = useState(workDoneInitial);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [csrfToken, setCsrfToken] = useState("");

  async function loadPortal() {
    const meResponse = await fetch("/api/employee/me/", { credentials: "include" });
    if (!meResponse.ok) return;
    const me = await meResponse.json();
    setEmployee(me);
    if (me.csrfToken) setCsrfToken(me.csrfToken);

    const workResponse = await fetch("/api/employee/work-orders/", { credentials: "include" });
    if (workResponse.ok) {
      const orders = await workResponse.json();
      setWorkOrders(orders);
      if (orders.length && !workDone.work_order) {
        setWorkDone((current) => ({ ...current, work_order: String(orders[0].id) }));
      }
    }

    const resourcesResponse = await fetch("/api/employee/resources/", { credentials: "include" });
    if (resourcesResponse.ok) {
      setResources(await resourcesResponse.json());
    }
  }

  useEffect(() => {
    loadPortal();
  }, []);

  function updateLogin(event) {
    const { name, value } = event.target;
    setLoginForm((current) => ({ ...current, [name]: value }));
  }

  function updateWorkDone(event) {
    const { name, value } = event.target;
    setWorkDone((current) => ({ ...current, [name]: value }));
  }

  async function submitLogin(event) {
    event.preventDefault();
    setStatus("logging-in");
    setMessage("");
    try {
      const response = await fetch("/api/employee/login/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Login failed");
      setEmployee(data.employee);
      if (data.csrfToken) setCsrfToken(data.csrfToken);
      setLoginForm(loginInitial);
      setStatus("idle");
      await loadPortal();
    } catch (error) {
      setStatus("error");
      setMessage(error.message);
    }
  }

  async function submitWorkDone(event) {
    event.preventDefault();
    setStatus("saving");
    setMessage("");
    try {
      const payload = {
        ...workDone,
        work_order: Number(workDone.work_order),
        container_work: workDone.container_work ? Number(workDone.container_work) : null,
        hours_worked: Number(workDone.hours_worked),
        containers_completed: Number(workDone.containers_completed),
        pallets_built: Number(workDone.pallets_built),
        pieces_handled: Number(workDone.pieces_handled),
      };
      const response = await fetch("/api/employee/work-done/", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken || getCookie("csrftoken") },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Could not save work done");
      setStatus("saved");
      setMessage("Work done was submitted.");
      setWorkDone((current) => ({ ...workDoneInitial, work_order: current.work_order, work_date: current.work_date }));
    } catch (error) {
      setStatus("error");
      setMessage(error.message);
    }
  }

  async function signOut() {
    await fetch("/api/employee/logout/", { method: "POST", credentials: "include", headers: { "X-CSRFToken": csrfToken || getCookie("csrftoken") } });
    setEmployee(null);
    setCsrfToken("");
    setWorkOrders([]);
    setResources({ notices: [], infoLinks: [], payStubs: [] });
    setWorkDone(workDoneInitial);
  }

  const selectedOrder = workOrders.find((order) => String(order.id) === String(workDone.work_order));

  if (!employee) {
    return (
      <section className="employee-portal page-hero compact">
        <p className="eyebrow">Employee Portal</p>
        <h1>Loader login for assigned work orders.</h1>
        <p>Employees can sign in to view assigned jobs and submit containers, hours, pallets, pieces, and job notes.</p>
        <form className="employee-login-form" onSubmit={submitLogin}>
          <label>Username<input name="username" value={loginForm.username} onChange={updateLogin} autoComplete="username" required /></label>
          <label>Password<input name="password" type="password" value={loginForm.password} onChange={updateLogin} autoComplete="current-password" required /></label>
          <button className="primary-button" type="submit" disabled={status === "logging-in"}>{status === "logging-in" ? "Signing in..." : "Sign In"}</button>
          {message && <p className="form-note error">{message}</p>}
        </form>
      </section>
    );
  }

  return (
    <section className="employee-portal page-hero">
      <div className="employee-header">
        <div>
          <p className="eyebrow">Employee Portal</p>
          <h1>Welcome, {employee.name}.</h1>
          <p>Review assigned work orders and submit work completed from the dock.</p>
        </div>
        <button className="secondary-button" type="button" onClick={signOut}>Sign Out</button>
      </div>

      <div className="employee-grid">
        <div className="employee-panel">
          <h2>Assigned Work Orders</h2>
          {workOrders.length === 0 && <p>No assigned work orders are available.</p>}
          <div className="work-order-list">
            {workOrders.map((order) => (
              <button key={order.id} type="button" className={String(order.id) === String(workDone.work_order) ? "selected" : ""} onClick={() => setWorkDone((current) => ({ ...current, work_order: String(order.id), container_work: "" }))}>
                <strong>{order.work_order_number}</strong>
                <span>{order.company_name || "Customer"} - {order.location}</span>
                <span>{order.work_date} - {order.status}</span>
              </button>
            ))}
          </div>
        </div>

        <form className="quote-form work-done-form" onSubmit={submitWorkDone}>
          <h2 className="full-row">Add Work Done</h2>
          <label>Work Order<select name="work_order" value={workDone.work_order} onChange={updateWorkDone} required>{workOrders.map((order) => <option key={order.id} value={order.id}>{order.work_order_number}</option>)}</select></label>
          <label>Container<select name="container_work" value={workDone.container_work} onChange={updateWorkDone}><option value="">General job entry</option>{selectedOrder?.containers?.map((container) => <option key={container.id} value={container.id}>{container.container_number}</option>)}</select></label>
          <label>Date<input name="work_date" type="date" value={workDone.work_date} onChange={updateWorkDone} required /></label>
          <label>Hours<input name="hours_worked" type="number" min="0" step="0.25" value={workDone.hours_worked} onChange={updateWorkDone} /></label>
          <label>Containers Completed<input name="containers_completed" type="number" min="0" value={workDone.containers_completed} onChange={updateWorkDone} /></label>
          <label>Pallets Built<input name="pallets_built" type="number" min="0" value={workDone.pallets_built} onChange={updateWorkDone} /></label>
          <label>Pieces Handled<input name="pieces_handled" type="number" min="0" value={workDone.pieces_handled} onChange={updateWorkDone} /></label>
          <label className="full-row">Notes<textarea name="notes" rows="4" value={workDone.notes} onChange={updateWorkDone} /></label>
          <button className="primary-button" type="submit" disabled={status === "saving" || !workOrders.length}>{status === "saving" ? "Saving..." : "Submit Work Done"}</button>
          {message && <p className={status === "saved" ? "form-note success" : "form-note error"}>{message}</p>}
        </form>
      </div>

      <div className="employee-resource-grid">
        <section className="employee-panel">
          <h2>Current Notices</h2>
          {resources.notices.length === 0 && <p>No current notices.</p>}
          <div className="employee-card-list">
            {resources.notices.map((notice) => (
              <article key={notice.id}>
                <strong>{notice.title}</strong>
                <span>{notice.notice_type_display}</span>
                <p>{notice.message}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="employee-panel">
          <h2>Info Links</h2>
          {resources.infoLinks.length === 0 && <p>No info links available.</p>}
          <div className="employee-card-list">
            {resources.infoLinks.map((link) => (
              <article key={link.id}>
                <strong>{link.title}</strong>
                <span>{link.link_type_display}</span>
                <p>{link.description}</p>
                <a href={link.url} target="_blank" rel="noreferrer">Open Link</a>
              </article>
            ))}
          </div>
        </section>

        <section className="employee-panel">
          <h2>Pay Stubs</h2>
          {resources.payStubs.length === 0 && <p>No pay stubs available.</p>}
          <div className="employee-card-list">
            {resources.payStubs.map((stub) => (
              <article key={stub.id}>
                <strong>{stub.pay_period_start} to {stub.pay_period_end}</strong>
                <span>Pay date: {stub.pay_date}</span>
                <p>Hours: {stub.hours} | Gross: ${stub.gross_pay} | Deductions: ${stub.deductions} | Net: ${stub.net_pay}</p>
                {stub.document_url && <a href={stub.document_url} target="_blank" rel="noreferrer">View Stub</a>}
              </article>
            ))}
          </div>
        </section>
      </div>
    </section>
  );
}
